import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logbookApi } from '../api/logbookApi';
import TopBar from '../components/TopBar';
import NewEntryModal from '../components/NewEntryModal';
import './LogbookDetailPage.css';
import EditEntryModal from '../components/EditEntryModal';
import { useAuth } from '../context/AuthContext';

const STATUS_CONFIG = {
  open:   { label: 'Nyitott', color: '#2bb344', bg: '#e8f9eb', icon: 'ti-circle-check' },
  saved:  { label: 'Mentett', color: '#b8862e', bg: '#fdf3e3', icon: 'ti-pause' },
  closed: { label: 'Lezárt',  color: '#8a958c', bg: '#f0f2f0', icon: 'ti-lock' },
};

const CATEGORY_CONFIG = {
  other:        { label: 'Egyéb / parkoló', icon: 'ti-parking',   color: '#8a958c' },
  pickup:       { label: 'Felrakó',    icon: 'ti-package',        color: '#2bb344' },
  delivery:     { label: 'Lerakó',     icon: 'ti-package-export', color: '#b8862e' },
  customs:      { label: 'Vámkezelés', icon: 'ti-stamp',          color: '#9b59b6' },
  refuel:       { label: 'Tankolás',   icon: 'ti-gas-station',    color: '#d9342b' },
};

function formatDateTime(dt) {
  if (!dt) return '–';
  return new Date(dt).toLocaleString('hu-HU', {
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function LogbookDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [logbook, setLogbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [closing, setClosing] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const { user } = useAuth();
  const [deleting, setDeleting] = useState(false);

  const fetchLogbook = async () => {
    try {
      const res = await logbookApi.get(id);
      setLogbook(res.data);
    } catch {
      setError('Nem sikerült betölteni a menetlevelet.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogbook(); }, [id]);

  const handleClose = async () => {
    if (!window.confirm('Biztosan lezárod a menetlevelet?')) return;
    setClosing(true);
    try {
      await logbookApi.close(id);
      navigate('/');
    } catch {
      alert('Nem sikerült lezárni a menetlevelet.');
    } finally {
      setClosing(false);
    }
  };

  const handleCloseWithoutRefuel = async () => {
    if (!window.confirm('Biztosan lezárod tankolás nélkül?')) return;
    setClosing(true);
    try {
      await logbookApi.closeWithoutRefuel(id);
      navigate('/');
    } catch {
      alert('Nem sikerült lezárni a menetlevelet.');
    } finally {
      setClosing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Biztosan törlöd ezt a menetlevelet? Ez a művelet nem vonható vissza!')) return;
    setDeleting(true);
    try {
      await logbookApi.delete(id);
      navigate('/');
    } catch {
      alert('Nem sikerült törölni a menetlevelet.');
    } finally {
      setDeleting(false);
    }
  };

  const isActive = logbook?.status === 'open' || logbook?.status === 'saved';

  if (loading) return (
    <div className="app-shell">
      <TopBar />
      <div className="app-content"><div className="loading-box">Betöltés...</div></div>
    </div>
  );

  if (error) return (
    <div className="app-shell">
      <TopBar />
      <div className="app-content"><div className="error-box">{error}</div></div>
    </div>
  );

  const statusCfg = STATUS_CONFIG[logbook.status];

  return (
    <div className="app-shell">
      <TopBar />
      <div className="app-content">

        {/* Fejléc */}
        <div className="detail-header">
          <div className="detail-header-top">
            <button className="btn-back" onClick={() => navigate('/')}>
              <i className="ti ti-arrow-left"></i>
            </button>
            <div className="detail-header-info">
              <div className="detail-vehicle">
                <i className="ti ti-truck"></i>
                {logbook.vehicle_plate}
              </div>
              <div className="detail-identifier">{logbook.identifier}</div>
            </div>
            <span className="status-badge" style={{ color: statusCfg.color, background: statusCfg.bg }}>
              <i className={`ti ${statusCfg.icon}`}></i>
              {statusCfg.label}
            </span>
          </div>

          {user?.is_staff && (
            <div className="detail-admin-actions">
              <button className="btn-delete" onClick={handleDelete} disabled={deleting}>
                <i className="ti ti-trash"></i>
                {deleting ? 'Törlés...' : 'Menetlevél törlése'}
              </button>
            </div>
          )}
        </div>

        {/* Bejegyzések listája */}
        {logbook.entries.length === 0 ? (
          <div className="empty-state">
            <i className="ti ti-clipboard-text"></i>
            <p>Még nincs bejegyzés.</p>
            <span>Add hozzá az első bejegyzést!</span>
          </div>
        ) : (
          <div className="entries-timeline">
            {logbook.entries.map((entry, idx) => {
              const cfg = CATEGORY_CONFIG[entry.category] || {};

              // Meghatározzuk, van-e üres mező
              const hasEmptyFields =
                !entry.arrival ||
                !entry.departure ||
                (entry.category === 'refuel' && (!entry.diesel));

              const isEditable = isActive && hasEmptyFields;

              return (
                <div className="entry-item" key={entry.id}>
                  <div className="entry-line">
                    <div className="entry-dot" style={{ background: cfg.color }}></div>
                    {idx < logbook.entries.length - 1 && <div className="entry-connector"></div>}
                  </div>
                  <div className="entry-card">
                    <div className="entry-card-top">
                      <span className="entry-label" style={{ color: cfg.color }}>
                        <i className={`ti ${cfg.icon}`}></i>
                        {cfg.label}
                      </span>
                      <div className="entry-card-top-right">
                        <span className="entry-km-badge">
                          {entry.km?.toLocaleString('hu-HU')} km
                        </span>
                        {isEditable && (
                          <button
                            className="btn-edit-entry"
                            onClick={() => setEditingEntry(entry)}
                            title="Módosítás"
                          >
                            <i className="ti ti-pencil"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="entry-city-name">{entry.city}</div>
                    <div className="entry-times">
                      {entry.arrival ? (
                        <span><i className="ti ti-login"></i> {formatDateTime(entry.arrival)}</span>
                      ) : (
                        <span className="entry-missing"><i className="ti ti-login"></i> Érkezés hiányzik</span>
                      )}
                      {entry.departure ? (
                        <span><i className="ti ti-logout"></i> {formatDateTime(entry.departure)}</span>
                      ) : (
                        <span className="entry-missing"><i className="ti ti-logout"></i> Távozás hiányzik</span>
                      )}
                    </div>
                    {entry.category === 'refuel' && (
                      <div className="entry-refuel">
                        {entry.diesel
                          ? <span><i className="ti ti-droplet"></i> Dízel: {entry.diesel} l</span>
                          : <span className="entry-missing"><i className="ti ti-droplet"></i> Dízel hiányzik</span>
                        }
                        {entry.adblue
                          ? <span><i className="ti ti-droplet-half-2"></i> AdBlue: {entry.adblue} l</span>
                          : null
                        }
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Összesítő – csak lezárt menetlevélnél */}
            {logbook.status === 'closed' && logbook.summary && (
              <div className="logbook-summary">
                <h3 className="summary-title">
                  <i className="ti ti-chart-bar"></i> Összesítő
                </h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Nyitó km</span>
                    <span className="summary-value">
                      {logbook.summary.opening_km?.toLocaleString('hu-HU')} km
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Záró km</span>
                    <span className="summary-value">
                      {logbook.summary.closing_km?.toLocaleString('hu-HU')} km
                    </span>
                  </div>
                  <div className="summary-item highlight">
                    <span className="summary-label">Megtett távolság</span>
                    <span className="summary-value">
                      {logbook.summary.distance?.toLocaleString('hu-HU')} km
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Tankolt dízel</span>
                    <span className="summary-value">
                      {logbook.summary.total_diesel} l
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Tankolt AdBlue</span>
                    <span className="summary-value">
                      {logbook.summary.total_adblue} l
                    </span>
                  </div>
                  <div className="summary-item highlight">
                    <span className="summary-label">Fogyasztás</span>
                    <span className="summary-value">
                      {logbook.summary.consumption
                        ? `${logbook.summary.consumption} l/100km`
                        : '–'}
                    </span>
                  </div>
                </div>
              </div>
              )}
          </div>
        )}

        {/* Akció gombok */}
        {isActive && (
          <div className="detail-actions">
            <button className="btn-add-entry" onClick={() => setShowEntryModal(true)}>
              <i className="ti ti-plus"></i> Új bejegyzés
            </button>
            <div className="detail-close-actions">
              <button className="btn-close-refuel" onClick={handleClose} disabled={closing}>
                <i className="ti ti-gas-station"></i>
                Lezárás tankolással
              </button>
              <button className="btn-close-no-refuel" onClick={handleCloseWithoutRefuel} disabled={closing}>
                <i className="ti ti-lock"></i>
                Lezárás tankolás nélkül
              </button>
            </div>
          </div>
        )}

      </div>

      {showEntryModal && (
        <NewEntryModal
          logbookId={id}
          onClose={() => setShowEntryModal(false)}
          onCreated={() => {
            setShowEntryModal(false);
            fetchLogbook();
          }}
        />
      )}

      {editingEntry && (
        <EditEntryModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={() => {
            setEditingEntry(null);
            fetchLogbook();
          }}
        />
      )}
    </div>
  );
}